// Insurance Eligibility Verification — Type Definitions
//
// Covers the three layers of the eligibility feature:
//   1. Payer   — the `payers` Firestore collection (seeded from Stedi's payer directory)
//   2. Wire    — the request/response shapes exchanged with Stedi's real-time
//                eligibility check API (X12 270/271 rendered as JSON)
//   3. Domain  — the normalized `EligibilityCheck` the UI renders and we persist
//
// NOTE: The wire types model Stedi's documented eligibility check response.
// They are deliberately loose (almost everything optional) because payers return
// wildly inconsistent subsets of the 271. Validate against a live sandbox call
// before relying on any field in Phase 2.

// ─── Service Type Codes ───────────────────────────────────────────────

/**
 * Service Type Codes we send in the 270 request.
 * Payers do not accept multiple STCs in one request — send exactly one and
 * fall back through the list if the payer rejects or returns nothing useful.
 */
export type ServiceTypeCode =
  | '30' // Health Benefit Plan Coverage (general baseline)
  | 'MH' // Mental Health
  | 'A4' // Psychiatric
  | 'A6' // Psychotherapy
  | 'CF'; // Mental Health Provider — Outpatient

export const SERVICE_TYPE_CODE_LABELS: Record<ServiceTypeCode, string> = {
  '30': 'Health Benefit Plan Coverage',
  MH: 'Mental Health',
  A4: 'Psychiatric',
  A6: 'Psychotherapy',
  CF: 'Mental Health Provider — Outpatient',
};

/**
 * Probe order for `stcTester` (Phase 2) and for runtime fallback.
 * '30' first because every payer supports it; the behavioral codes carry
 * richer benefit detail when the payer honors them.
 */
export const SERVICE_TYPE_CODE_PROBE_ORDER: ServiceTypeCode[] = ['30', 'MH', 'A6', 'A4', 'CF'];

/** Default STC used when a payer has no discovered optimal code yet. */
export const DEFAULT_SERVICE_TYPE_CODE: ServiceTypeCode = '30';

// ─── Payer (Firestore `payers` collection) ────────────────────────────

export interface PayerTransactionSupport {
  eligibilityCheck: boolean;
  claimStatus?: boolean;
  claimSubmission?: boolean;
  coordinationOfBenefits?: boolean;
}

/** Result of probing one STC against one payer (written by `stcTester`). */
export interface ServiceTypeCodeProbe {
  serviceTypeCode: ServiceTypeCode;
  succeeded: boolean;
  benefitCount: number;
  hasCostShare: boolean;
  errorCode?: string;
  probedAt: Date;
}

export interface Payer {
  id: string;
  /** The `tradingPartnerServiceId` Stedi expects. Not the same as a legacy CPID. */
  stediPayerId: string;
  name: string;
  displayName?: string;
  /** Alternate names/abbreviations, lowercased — powers PayerPicker search. */
  aliases?: string[];
  address?: string;
  transactionSupport: PayerTransactionSupport;
  /** Some payers require provider enrollment before eligibility calls succeed. */
  enrollmentRequired: boolean;
  /** True when this payer IS a behavioral carve-out administrator (Magellan, Carelon, Optum Behavioral). */
  isMbho: boolean;
  /** stediPayerId of the MBHO this payer is known to carve behavioral benefits out to. */
  knownCarveOutTo?: string;
  /** Best-performing STC for this payer, discovered by `stcTester`. */
  optimalServiceTypeCode?: ServiceTypeCode;
  serviceTypeCodeProbes?: ServiceTypeCodeProbe[];
  /** Where this record came from. `static` rows are the payersData.ts fallback, deleted in Phase 5. */
  source: 'stedi' | 'static' | 'manual';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ─── Wire: 270 request ────────────────────────────────────────────────

export interface EligibilityRequestProvider {
  organizationName?: string;
  firstName?: string;
  lastName?: string;
  npi: string;
}

export interface EligibilityRequestSubscriber {
  memberId?: string;
  firstName?: string;
  lastName?: string;
  /** YYYYMMDD — Stedi rejects ISO dates on this field. */
  dateOfBirth?: string;
  ssn?: string;
  groupNumber?: string;
}

export interface EligibilityRequestDependent {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  relationshipToSubscriber?: string;
}

export interface EligibilityRequestEncounter {
  /** One code per request. Payers reject multi-code requests. */
  serviceTypeCodes: string[];
  /** YYYYMMDD */
  dateOfService?: string;
  beginningDateOfService?: string;
  endDateOfService?: string;
}

export interface EligibilityRequest {
  controlNumber: string;
  tradingPartnerServiceId: string;
  provider: EligibilityRequestProvider;
  subscriber: EligibilityRequestSubscriber;
  dependent?: EligibilityRequestDependent;
  encounter: EligibilityRequestEncounter;
}

// ─── Wire: 271 response ───────────────────────────────────────────────

/**
 * The carve-out carrier. When a payer administers behavioral benefits through
 * an MBHO, the entity appears here with the member ID to use on the second pass.
 */
export interface BenefitsRelatedEntity {
  entityIdentifier?: string;
  entityType?: string;
  entityName?: string;
  entityFirstname?: string;
  entityIdentification?: string;
  entityIdentificationValue?: string;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  contactInformation?: {
    name?: string;
    contacts?: Array<{
      communicationMode?: string;
      communicationNumber?: string;
    }>;
  };
}

export interface EligibilityAdditionalInformation {
  codeListQualifierCode?: string;
  /** Place-of-service and other industry codes. '02'/'10' signal telehealth. */
  industryCode?: string;
  codeCategory?: string;
}

export interface BenefitsInformation {
  /** See BENEFIT_CODES — '1' active, '6' inactive, 'B' copay, 'C' deductible, etc. */
  code?: string;
  name?: string;
  coverageLevelCode?: string;
  coverageLevel?: string;
  serviceTypeCodes?: string[];
  serviceTypes?: string[];
  insuranceTypeCode?: string;
  insuranceType?: string;
  planCoverage?: string;
  timeQualifierCode?: string;
  timeQualifier?: string;
  benefitAmount?: string;
  benefitPercent?: string;
  quantityQualifierCode?: string;
  quantityQualifier?: string;
  benefitQuantity?: string;
  /** 'Y' when the service requires prior authorization. */
  authOrCertIndicator?: string;
  inPlanNetworkIndicatorCode?: string;
  inPlanNetworkIndicator?: string;
  headerLoopIdentifierCode?: string;
  trailerLoopIdentifierCode?: string;
  benefitsRelatedEntity?: BenefitsRelatedEntity;
  benefitsRelatedEntities?: BenefitsRelatedEntity[];
  benefitsDateInformation?: Record<string, string>;
  eligibilityAdditionalInformationList?: EligibilityAdditionalInformation[];
  additionalInformation?: Array<{ description?: string }>;
}

export interface EligibilityResponseError {
  field?: string;
  code?: string;
  description?: string;
  followupAction?: string;
  location?: string;
  possibleResolutions?: string;
}

export interface EligibilityResponsePayer {
  entityIdentifier?: string;
  entityType?: string;
  name?: string;
  payorIdentification?: string;
  contactInformation?: BenefitsRelatedEntity['contactInformation'];
}

export interface EligibilityResponsePlanStatus {
  statusCode?: string;
  status?: string;
  planDetails?: string;
  serviceTypeCodes?: string[];
}

/** The 271 as Stedi returns it. Persisted encrypted in `eligibility_checks.rawResponse`. */
export interface EligibilityResponse {
  meta?: {
    senderId?: string;
    submitterId?: string;
    billerId?: string;
    traceId?: string;
    applicationMode?: string;
  };
  controlNumber?: string;
  reassociationKey?: string;
  tradingPartnerServiceId?: string;
  provider?: EligibilityRequestProvider & { providerOrgName?: string };
  subscriber?: EligibilityRequestSubscriber & {
    middleName?: string;
    gender?: string;
    entityIdentifier?: string;
    address?: BenefitsRelatedEntity['address'];
  };
  dependents?: Array<Record<string, unknown>>;
  payer?: EligibilityResponsePayer;
  planInformation?: Record<string, string>;
  planDateInformation?: Record<string, string>;
  planStatus?: EligibilityResponsePlanStatus[];
  benefitsInformation?: BenefitsInformation[];
  errors?: EligibilityResponseError[];
  x12?: string;
}

// ─── 271 code tables ──────────────────────────────────────────────────

/**
 * Eligibility/benefit codes from the 271 EB01 element.
 * Note 'A' is Co-Insurance; "active, network only" is signalled by
 * inPlanNetworkIndicatorCode on an ACTIVE ('1') entry, not by a separate code.
 */
export const BENEFIT_CODES = {
  ACTIVE: '1',
  COINSURANCE: 'A',
  COPAYMENT: 'B',
  DEDUCTIBLE: 'C',
  LIMITATIONS: 'F',
  OUT_OF_POCKET: 'G',
  INACTIVE: '6',
  INACTIVE_PENDING: '7',
  NON_COVERED: 'I',
  COST_CONTAINMENT: 'M',
  OTHER_SOURCE_OF_DATA: 'R',
  BENEFIT_DISCLAIMER: 'U',
  CANNOT_PROCESS: 'V',
} as const;

/** Place-of-service codes that mean the plan pays for telehealth. */
export const TELEHEALTH_PLACE_OF_SERVICE_CODES = ['02', '10'];

/** Free-text markers payers use instead of a POS code. Matched case-insensitively. */
export const TELEHEALTH_KEYWORDS = [
  'VIRTUAL VISIT',
  'TELEHEALTH',
  'TELEMEDICINE',
  'E-VISIT',
  'EVISIT',
  'ONLINE VISIT',
];

/**
 * Entity identifiers in `benefitsRelatedEntities` that indicate benefits are
 * administered elsewhere — the carve-out signal.
 */
export const CARVE_OUT_ENTITY_IDENTIFIERS = [
  'Payer',
  'Third-Party Administrator',
  'Utilization Management Organization',
  'Plan Sponsor',
];

// ─── Domain: normalized check ─────────────────────────────────────────

export type CoverageStatus = 'active' | 'inactive' | 'unknown' | 'error';

export type EligibilityCheckSource = 'stedi' | 'stedi-sandbox' | 'mock' | 'cache';

export interface NormalizedBenefits {
  copay?: number;
  coinsurancePercent?: number;
  deductibleTotal?: number;
  deductibleRemaining?: number;
  outOfPocketTotal?: number;
  outOfPocketRemaining?: number;
  visitLimitTotal?: number;
  visitLimitRemaining?: number;
  /** null when the payer said nothing either way — render "Unknown", not "No". */
  priorAuthRequired?: boolean | null;
  telehealthCovered?: boolean | null;
  /** What we matched on, so the UI can show why we claim telehealth is covered. */
  telehealthEvidence?: string;
  inNetwork?: boolean | null;
  planName?: string;
  planBeginDate?: string;
  planEndDate?: string;
  groupNumber?: string;
}

export interface CarveOutInfo {
  detected: boolean;
  entityName?: string;
  payerIdentifier?: string;
  /** Member ID returned by the primary payer, used for the second-pass call. */
  memberId?: string;
  phone?: string;
  /** Matching `payers` doc, when we could resolve one. */
  resolvedPayerId?: string;
  secondPassPerformed: boolean;
  secondPassStatus?: CoverageStatus;
  secondPassError?: string;
}

/**
 * One eligibility verification, persisted in `eligibility_checks`.
 * Written server-side only (Firestore rules deny client writes).
 */
export interface EligibilityCheck {
  id: string;
  patientId: string;
  providerId: string;
  practiceId: string;
  payerId: string;
  payerName: string;
  stediPayerId: string;
  serviceTypeCode: ServiceTypeCode;
  status: CoverageStatus;
  /** Human-readable reason, especially for `inactive` and `error`. */
  statusReason?: string;
  benefits: NormalizedBenefits;
  carveOut: CarveOutInfo;
  /** Encrypted 271 from the primary payer. Never write plaintext here. */
  rawResponse?: string;
  /** Encrypted 271 from the MBHO second pass, when one happened. */
  rawCarveOutResponse?: string;
  rawResponseEncrypted: boolean;
  source: EligibilityCheckSource;
  checkedAt: Date;
  checkedBy: string;
  /** checkedAt + cache window. Reads newer than this skip a billable Stedi call. */
  expiresAt: Date;
  controlNumber?: string;
  traceId?: string;
  errors?: EligibilityResponseError[];
}

/**
 * What the UI passes to EligibilityService — no wire details.
 * Payer identity is passed in rather than looked up: PayerPicker already holds
 * the selected `Payer` record, so the service does not re-read Firestore.
 */
export interface VerifyEligibilityInput {
  patientId: string;
  payerId: string;
  payerName: string;
  stediPayerId: string;
  providerId: string;
  /** NPI billed under. Required by the payer, not optional in practice. */
  providerNpi: string;
  providerOrganizationName?: string;
  practiceId: string;
  memberId: string;
  firstName: string;
  lastName: string;
  /** ISO (YYYY-MM-DD). Converted to YYYYMMDD at the wire boundary. */
  dateOfBirth: string;
  serviceTypeCode?: ServiceTypeCode;
  dateOfService?: string;
  /** Skip the 24h cache and force a fresh (billable) call. */
  forceRefresh?: boolean;
}

export interface EligibilityCheckResult {
  check: EligibilityCheck;
  fromCache: boolean;
}

/** Cache window shared by the service, the endpoint, and the reminder cron. */
export const ELIGIBILITY_CACHE_HOURS = 24;

/** Re-verify before an appointment when the last check is older than this. */
export const ELIGIBILITY_STALE_DAYS = 7;
